import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { ADD_CANDIDATE_TO_CAMPAIGN } from "@lib/graphql/mutations";
import { FIND_CANDIDATES_FOR_ROLE_QUERY } from "@lib/graphql/queries";
import { Sparkles, UserPlus } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "urql";

interface FindCandidatesPanelProps {
  campaignId: string;
  onAdded: () => void;
}

interface CandidateResult {
  id: string;
  fullName: string;
  referenceNumber?: number;
  email: string | null;
  currentRoleTitle: string | null;
  currentCompany: string | null;
  location: string | null;
}

export function FindCandidatesPanel({ campaignId, onAdded }: FindCandidatesPanelProps) {
  const [active, setActive] = useState(false);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  const [{ data, fetching }] = useQuery({
    query: FIND_CANDIDATES_FOR_ROLE_QUERY,
    variables: { campaignId, limit: 20 },
    pause: !active,
  });
  const [, addToCanpaign] = useMutation(ADD_CANDIDATE_TO_CAMPAIGN);

  const candidates: CandidateResult[] = data?.findCandidatesForRole ?? [];

  const handleAdd = async (candidateId: string) => {
    await addToCanpaign({
      input: { campaignId, candidateId, stage: "longlist", source: "ai_match" },
    });
    setAddedIds(new Set([...addedIds, candidateId]));
    onAdded();
  };

  if (!active) {
    return (
      <button
        type="button"
        onClick={() => setActive(true)}
        className="flex items-center gap-2 rounded border border-dashed border-border px-4 py-3 text-xs text-text-muted hover:border-accent hover:text-accent"
      >
        <Sparkles size={14} /> Find matching candidates for this role
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-medium text-text-secondary">
          <Sparkles size={14} className="text-accent" /> Role-Matched Candidates
        </h4>
        <button
          type="button"
          onClick={() => setActive(false)}
          className="text-xs text-text-faint hover:text-text-secondary"
        >
          Close
        </button>
      </div>

      {fetching ? (
        <p className="py-4 text-center text-xs text-text-faint">Searching...</p>
      ) : candidates.length === 0 ? (
        <p className="py-4 text-center text-xs text-text-faint">
          No matches found. Ensure the role blueprint has been set up.
        </p>
      ) : (
        <div className="space-y-1">
          {candidates.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 rounded border border-border px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                {typeof c.referenceNumber === "number" && (
                  <>
                    <CandidateReferenceBadge referenceNumber={c.referenceNumber} />{" "}
                  </>
                )}
                <span className="text-sm font-medium text-text-primary">{c.fullName}</span>
                {(c.currentRoleTitle || c.currentCompany) && (
                  <span className="ml-2 text-xs text-text-muted">
                    {[c.currentRoleTitle, c.currentCompany].filter(Boolean).join(" at ")}
                  </span>
                )}
                {c.location && <span className="ml-2 text-xs text-text-faint">{c.location}</span>}
              </div>
              {addedIds.has(c.id) ? (
                <span className="text-xs text-green-600">Added</span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleAdd(c.id)}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
                >
                  <UserPlus size={12} /> Add
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
